import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faFileCsv, faDownload } from '@fortawesome/free-solid-svg-icons';
import { Button, Col, ListGroup, Modal, ProgressBar, Row, Spinner } from 'react-bootstrap';

import { createUserBatch, inviteBatchUsers, resetUserBatch } from '../../../../actions/user.action';

const UserBatch = ({handleExportUsersTemplate}) => {
  const dispatch = useDispatch();
  const storeJwt = useSelector((state) => state.session.jwt);
  const {
    isProcessingBatch,
    isProcessingExport,
    batchProgress,
    batchErrors,
    batchExistingUsers,
    batchExistingMembers,
  } = useSelector((state) => state.users);

  const [showModal, setShowModal] = useState(false);
  const [fileContent, setFileContent] = useState();
  const [userBatch, setUsersBatch] = useState([]);
  const [invitingExisting, setInvitingExisting] = useState(false);
  const [invitingEmail, setInvitingEmail] = useState(null);
  const [invitedEmails, setInvitedEmails] = useState([]);
  const [inviteExistingError, setInviteExistingError] = useState(null);
  const fileRef = useRef();

  const resetInviteState = () => {
    setInvitedEmails([]);
    setInvitingEmail(null);
    setInviteExistingError(null);
  };

  const handleClose = () => {
    if (!isProcessingBatch && !invitingExisting && !invitingEmail) {
      setShowModal(false);
      resetInviteState();
      dispatch(resetUserBatch());
    }
  };

  // Rows in the file that actually produced a new user: neither an existing
  // member of this organization nor someone who has to be invited from another.
  const createdCount =
    userBatch.length - (batchExistingUsers?.length || 0) - (batchExistingMembers?.length || 0);

  const handleCreateBatchUsers = () => setShowModal(true);
  const handleUploadClick = () => {
    if (fileRef?.current) {
      setUsersBatch([]);
      resetInviteState();
      fileRef.current.value = null;
      fileRef.current.click();
    }
  };

  const handleInviteUser = async (existingUser) => {
    setInvitingEmail(existingUser.email);
    setInviteExistingError(null);
    try {
      await dispatch(inviteBatchUsers([existingUser], storeJwt));
      setInvitedEmails((prev) => [...prev, existingUser.email]);
    } catch (e) {
      setInviteExistingError(e?.response?.data?.message || e?.message || 'Unexpected Error!');
    } finally {
      setInvitingEmail(null);
    }
  };

  const handleSendInvites = async () => {
    const pending = batchExistingUsers.filter((u) => !invitedEmails.includes(u.email));
    setInvitingExisting(true);
    setInviteExistingError(null);
    try {
      await dispatch(inviteBatchUsers(pending, storeJwt));
      setInvitedEmails((prev) => [...prev, ...pending.map((u) => u.email)]);
    } catch (e) {
      setInviteExistingError(e?.response?.data?.message || e?.message || 'Unexpected Error!');
    } finally {
      setInvitingExisting(false);
    }
  };

  const handleFileChange = () => {
    const fr = new FileReader();
    fr.onload = async (e) => {
      const text = e.target.result;
      setFileContent(text);
    };
    fr.readAsText(fileRef.current.files[0]);
  };

  const handleProcessUsers = async () => {
    if (userBatch.length > 0) {
      resetInviteState();
      const usersToProcess = userBatch.map((ub) => ({
        username: ub[2],
        email: ub[2],
        firstName: ub[0],
        lastName: ub[1],
        status: 'ACTIVE',
        role: ub[4],
        state: '',
        organizations: ub[3] && ub[3] !== '' ? ub[3].split(';').map((i) => i.trim()) : [],
        lastLogin: new Date(),
        institution: ub.length === 5 ? ub[5] : '',
      }));
      dispatch(createUserBatch(usersToProcess, storeJwt));
      setFileContent('');
    }
  };

  useEffect(() => {
    if (fileContent && fileContent !== '') {
      const csvLines = fileContent
        .split(/[\r\n]+/g)
        .map((line) => line.trim())
        .filter((line) => line.length > 0);
      const usersBatch = csvLines.slice(1).map((r) => r.split(',').map((i) => i.trim()));
      setUsersBatch(usersBatch);
    }
  }, [fileContent]);

  return (
    <>
      <button
        title="Create Users Batch"
        className="btn btn-custom dark"
        type="button"
        onClick={handleCreateBatchUsers}
      >
        <FontAwesomeIcon icon={faUsers} />
      </button>
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Dialog>
          <Modal.Header closeButton>
            <Modal.Title>Create Users Batch</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col className="text-left">
                <p>Download and complete the batch user upload template. Then upload the file below. 
                  <Button
                  variant="success"
                  className="btn btn-custom success"
                  onClick={handleExportUsersTemplate}
                  disabled={isProcessingBatch || isProcessingExport}
                >
                  Download <FontAwesomeIcon className="ml-2" icon={faDownload} />
                </Button></p>
                <p>Upload batch file. (.csv)</p>
                <input ref={fileRef} className="d-none" type="file" onChange={handleFileChange} />
                <Button
                  variant="primary"
                  className="mb-5 btn btn-custom dark"
                  onClick={handleUploadClick}
                  disabled={isProcessingBatch || isProcessingExport}
                >
                  Upload File <FontAwesomeIcon className="ml-2" icon={faFileCsv} />
                </Button>
              </Col>
            </Row>
            {isProcessingBatch && (
              <Row>
                <Col>
                  <ProgressBar striped variant="info" now={batchProgress} />
                </Col>
              </Row>
            )}
            {batchErrors?.length > 0 && !isProcessingBatch && (
              <ListGroup>
                {batchErrors.map((error) => (
                  <ListGroup.Item key={error.username} variant="danger">
                    {error.username}: {error.message}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            {batchExistingMembers?.length > 0 && !isProcessingBatch && (
              <ListGroup className="mt-3">
                <ListGroup.Item variant="info">
                  The following users are already members of your organization. No action is
                  needed for them.
                </ListGroup.Item>
                {batchExistingMembers.map((user) => (
                  <ListGroup.Item key={user.username || user.email} variant="info">
                    {user.email}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
            {batchExistingUsers?.length > 0 && !isProcessingBatch && (
              <ListGroup className="mt-3">
                <ListGroup.Item variant="warning">
                  The following users already exist in another organization. Invite them to join
                  the organization(s) from the uploaded file.
                </ListGroup.Item>
                {batchExistingUsers.map((user) => {
                  const invited = invitedEmails.includes(user.email);
                  return (
                    <ListGroup.Item
                      key={user.username}
                      variant="warning"
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span>{user.email}</span>
                      {invited ? (
                        <span className="text-success">Invited</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="primary"
                          className="btn btn-custom dark"
                          onClick={() => handleInviteUser(user)}
                          disabled={invitingExisting || invitingEmail === user.email}
                        >
                          {invitingEmail === user.email ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            'Invite'
                          )}
                        </Button>
                      )}
                    </ListGroup.Item>
                  );
                })}
                {inviteExistingError && (
                  <ListGroup.Item variant="danger">{inviteExistingError}</ListGroup.Item>
                )}
                {batchExistingUsers.every((u) => invitedEmails.includes(u.email)) ? (
                  <ListGroup.Item variant="success">
                    Invitations sent to {batchExistingUsers.length} existing user(s).
                  </ListGroup.Item>
                ) : (
                  <div className="mt-2">
                    <Button
                      variant="primary"
                      className="btn btn-custom dark"
                      onClick={handleSendInvites}
                      disabled={invitingExisting || invitingEmail !== null}
                    >
                      {invitingExisting ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Send Invites to all'
                      )}
                    </Button>
                  </div>
                )}
              </ListGroup>
            )}
            {batchErrors?.length === 0 && batchProgress === 100 && createdCount > 0 && (
              <ListGroup>
                <ListGroup.Item variant="success">
                  Batch process complete successfully! New {createdCount} users created!
                </ListGroup.Item>
              </ListGroup>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              className="btn btn-custom white"
              disabled={isProcessingBatch || invitingExisting || invitingEmail !== null}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="btn btn-custom dark"
              disabled={isProcessingBatch || invitingExisting || invitingEmail !== null}
              onClick={handleProcessUsers}
            >
              Create Users
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal>
    </>
  );
};

export default UserBatch;
